import * as React from 'react';

import { toZoned } from '@internationalized/date';
import {
  enumPaymentMethod,
  PaymentMethod,
} from '@rematter/frontend-lib-api-client';
import {
  Button,
  Col,
  DatePicker,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogMain,
  DialogOverlay,
  DialogTitle,
  formatMoney,
  Row,
  TextArea,
  TextField,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';

import { receiveInvoiceAtom } from '../../_graphql/mutationReceiveInvoice';
import {
  $accountAllocations,
  $achNumber,
  $accountGroups,
  $applyAllocationMethod,
  $canSubmit,
  $checkFacilityId,
  $checkMemo,
  $checkNumber,
  $facilityIDs,
  $financialAccountId,
  $groupWireNumbers,
  $hasMixedCurrencies,
  $initPaymentItems,
  $isSubmitting,
  $itemAllocations,
  $paymentAmount,
  $paymentMethod,
  $resetPaymentAllocation,
  $selectedAllocationMethod,
  $showAllocationBanner,
  $transactionDate,
  $updateAccountAllocation,
  $updateAccountSelection,
  $updateGroupWireNumber,
  $updateItemAllocation,
  $updatePaymentMethod,
  $wireNumber,
  PAYMENT_METHOD_ACCOUNT_TYPES,
} from '../../_state/paymentAllocation.state';
import { useTranslations } from '../../i18n/useTranslations';
import { getCheckNumberForGroup, submitPaymentGroups } from '../../utils';
import { AccountGroupCard } from '../_shared/AccountGroupCard';
import { AllocationBanner } from '../_shared/AllocationBanner';

import { InvoiceRecord } from '~/modules/finance/types';
import { SelectEnum } from '~/shared/components/select-fields/SelectEnum';
import { SelectFacility } from '~/shared/components/select-fields/SelectFacility';
import { SelectFinancialAccount } from '~/shared/components/select-fields/SelectFinancialAccount';
import { store } from '~/shared/state';
import { $localization } from '~/shared/state/preferences.state';

interface InvoicePaymentDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  invoices: InvoiceRecord[];
  onSuccess?: () => void;
}

//
// ⚛️ ⚛️ ⚛️ INVOICE PAYMENT DRAWER ⚛️ ⚛️ ⚛️
//
export const InvoicePaymentDrawer = ({
  isOpen,
  onClose,
  invoices,
  onSuccess,
}: InvoicePaymentDrawerProps) => {
  // Translations
  const t = useTranslations();

  // Atoms
  const paymentMethod = useAtomValue($paymentMethod);
  const transactionDate = useAtomValue($transactionDate);
  const financialAccountId = useAtomValue($financialAccountId);
  const checkFacilityId = useAtomValue($checkFacilityId);
  const checkNumber = useAtomValue($checkNumber);
  const checkMemo = useAtomValue($checkMemo);
  const achNumber = useAtomValue($achNumber);
  const wireNumber = useAtomValue($wireNumber);
  const groupWireNumbers = useAtomValue($groupWireNumbers);
  const isSubmitting = useAtomValue($isSubmitting);
  const selectedAllocationMethod = useAtomValue($selectedAllocationMethod);
  const showAllocationBanner = useAtomValue($showAllocationBanner);
  const hasMixedCurrencies = useAtomValue($hasMixedCurrencies);
  const accountGroups = useAtomValue($accountGroups);
  const itemAllocations = useAtomValue($itemAllocations);
  const accountAllocations = useAtomValue($accountAllocations);
  const paymentAmount = useAtomValue($paymentAmount);
  const facilityIDs = useAtomValue($facilityIDs);
  const canSubmit = useAtomValue($canSubmit);
  const setTransactionDate = useSetAtom($transactionDate);
  const setCheckFacilityId = useSetAtom($checkFacilityId);
  const setCheckNumber = useSetAtom($checkNumber);
  const setCheckMemo = useSetAtom($checkMemo);
  const setAchNumber = useSetAtom($achNumber);
  const setWireNumber = useSetAtom($wireNumber);
  const setIsSubmitting = useSetAtom($isSubmitting);
  const setShowAllocationBanner = useSetAtom($showAllocationBanner);
  const setFinancialAccountId = useSetAtom($financialAccountId);
  const setItemAllocations = useSetAtom($itemAllocations);
  const initPaymentItems = useSetAtom($initPaymentItems);
  const resetPaymentAllocation = useSetAtom($resetPaymentAllocation);
  const updatePaymentMethod = useSetAtom($updatePaymentMethod);
  const updateAccountSelection = useSetAtom($updateAccountSelection);
  const updateItemAllocation = useSetAtom($updateItemAllocation);
  const updateAccountAllocation = useSetAtom($updateAccountAllocation);
  const updateGroupWireNumber = useSetAtom($updateGroupWireNumber);
  const applyAllocationMethod = useSetAtom($applyAllocationMethod);

  // Data hooks
  const toast = useToast();
  const receiveInvoice = useSetAtom(receiveInvoiceAtom());

  // Callbacks
  const handleClose = React.useCallback(() => {
    resetPaymentAllocation();
    onClose();
  }, [resetPaymentAllocation, onClose]);

  const handleSubmit = React.useCallback(async () => {
    if (
      !invoices.length ||
      !transactionDate ||
      paymentAmount <= 0 ||
      !financialAccountId
    )
      return;

    setIsSubmitting(true);
    try {
      const zonedDateTime = toZoned(
        transactionDate,
        store.get($localization).timezone ?? 'UTC',
      );
      const isMultiGroup = accountGroups.length > 1;

      await submitPaymentGroups({
        accountGroups,
        itemAllocations,
        buildMutation: (group, i) =>
          receiveInvoice({
            invoices: group.items
              .map((invoice) => ({
                invoiceId: invoice.id,
                appliedAmount: {
                  amount: itemAllocations.get(invoice.id) ?? 0,
                  currencyCode: invoice.amount.currencyCode,
                },
              }))
              .filter((item) => item.appliedAmount.amount > 0),
            paymentMethod,
            transactionDate: zonedDateTime,
            accountId:
              group.accountKey === '__no_account__'
                ? undefined
                : group.accountKey,
            financialAccountId,
            checkFacilityId: checkFacilityId || undefined,
            checkNumber: getCheckNumberForGroup(checkNumber, i) || undefined,
            checkMemo: checkMemo || undefined,
            achNumber: achNumber || undefined,
            wireNumber:
              (isMultiGroup
                ? groupWireNumbers.get(group.accountKey)
                : wireNumber) || undefined,
          }),
        onAllSuccess: () => {
          toast.success(t.invoices.paymentDrawer.messages.success);
          onSuccess?.();
          handleClose();
        },
        onPartialSuccess: (succeeded, total, fulfilledGroups) => {
          toast.warning(
            t.invoices.paymentDrawer.messages.partialSuccess(succeeded, total),
          );
          setItemAllocations((prev) => {
            const next = new Map(prev);
            for (const group of fulfilledGroups) {
              for (const item of group.items) next.set(item.id, 0);
            }
            return next;
          });
          onSuccess?.();
        },
        onAllFailed: (error) => {
          toast.error(
            t.invoices.paymentDrawer.messages.error(
              error instanceof Error
                ? error.message
                : t.messages.common.unknownError,
            ),
          );
        },
      });
    } catch (error) {
      toast.error(
        t.invoices.paymentDrawer.messages.error(
          error instanceof Error
            ? error.message
            : t.messages.common.unknownError,
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    invoices,
    transactionDate,
    paymentAmount,
    accountGroups,
    itemAllocations,
    paymentMethod,
    financialAccountId,
    checkFacilityId,
    checkNumber,
    checkMemo,
    achNumber,
    wireNumber,
    groupWireNumbers,
    setIsSubmitting,
    setItemAllocations,
    receiveInvoice,
    toast,
    onSuccess,
    handleClose,
    t,
  ]);

  // Effects
  /** Initialize payment items when drawer opens or invoices change */
  React.useEffect(() => {
    if (isOpen && invoices.length > 0) {
      initPaymentItems({
        items: invoices.map((invoice) => ({
          ...invoice,
          amount: invoice.openAmount ?? invoice.amount,
        })),
        unknownLabel: t.invoices.list.groupHeader.unknownAccount,
      });
    }
  }, [
    isOpen,
    invoices,
    initPaymentItems,
    t.invoices.list.groupHeader.unknownAccount,
  ]);

  if (!isOpen || !invoices.length) {
    return null;
  }

  const isMultipleInvoices = invoices.length > 1;
  const isCheckPayment = paymentMethod === 'Check';
  const isAchPayment = paymentMethod === 'ACH';
  const isMultiGroup = accountGroups.length > 1;
  const isWirePayment = paymentMethod === 'Wire';

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={onClose}
      className="@InvoicePaymentDrawer"
    >
      <DialogContent
        position="right"
        dialogClassName="w-96 grid-rows-[auto_1fr_auto]"
        className="@InvoicePaymentDrawer"
        isKeyboardDismissDisabled
      >
        <DialogHeader>
          <DialogTitle>
            {isMultipleInvoices
              ? t.invoices.paymentDrawer.title.multiple
              : t.invoices.paymentDrawer.title.single}
          </DialogTitle>
        </DialogHeader>

        <DialogMain>
          <Col gap4>
            <SelectEnum<PaymentMethod>
              label={t.invoices.paymentDrawer.fields.paymentMethod}
              enumValue={enumPaymentMethod}
              value={paymentMethod}
              onChange={updatePaymentMethod}
              isRequired
            />

            <DatePicker
              label={t.invoices.paymentDrawer.fields.transactionDate}
              value={transactionDate ?? undefined}
              onChange={setTransactionDate}
              isRequired
            />

            {isCheckPayment && (
              <>
                <SelectFacility
                  label={t.invoices.paymentDrawer.fields.checkFacility}
                  value={checkFacilityId ?? undefined}
                  onChange={(value) => setCheckFacilityId(value ?? null)}
                />
                <TextField
                  label={
                    isMultiGroup
                      ? t.invoices.paymentDrawer.fields.startingCheckNumber
                      : t.invoices.paymentDrawer.fields.checkNumber
                  }
                  value={checkNumber}
                  onChange={setCheckNumber}
                  description={
                    isMultiGroup && checkNumber
                      ? t.invoices.paymentDrawer.messages.checkAutoIncrement(
                          checkNumber,
                          getCheckNumberForGroup(
                            checkNumber,
                            accountGroups.length - 1,
                          ) ?? '',
                        )
                      : undefined
                  }
                />
                <TextArea
                  label={t.invoices.paymentDrawer.fields.checkMemo}
                  value={checkMemo}
                  onChange={setCheckMemo}
                  rows={2}
                />
              </>
            )}
            {isAchPayment && (
              <TextField
                label={t.invoices.paymentDrawer.fields.achNumber}
                value={achNumber ?? ''}
                onChange={(value) => setAchNumber(value || null)}
                isRequired
              />
            )}
            {isWirePayment && !isMultiGroup && (
              <TextField
                label={t.invoices.paymentDrawer.fields.wireNumber}
                value={wireNumber ?? ''}
                onChange={(value) => setWireNumber(value || null)}
              />
            )}

            <SelectFinancialAccount
              label={t.invoices.paymentDrawer.fields.financialAccount}
              value={financialAccountId ?? undefined}
              onChange={(value: string | undefined) =>
                setFinancialAccountId(value ?? null)
              }
              visibleInPayments={true}
              facilityIDs={facilityIDs}
              types={PAYMENT_METHOD_ACCOUNT_TYPES[paymentMethod]}
              onAccountSelect={updateAccountSelection}
              isRequired
            />

            {showAllocationBanner && (
              <AllocationBanner
                variant="invoice"
                selectedAllocationMethod={selectedAllocationMethod}
                onKeepCurrent={() => setShowAllocationBanner(false)}
                onApply={applyAllocationMethod}
              />
            )}

            {hasMixedCurrencies && (
              <div className="rounded bg-failure/10 px-3 py-2">
                <p className="text-sm text-failure">
                  {t.invoices.paymentDrawer.messages.mixedCurrency}
                </p>
              </div>
            )}
          </Col>

          <Col gap4 className="mt-6">
            {accountGroups.map((group) => {
              const groupAllocation =
                accountAllocations.get(group.accountKey) ?? 0;
              const isPartial = groupAllocation < group.total;

              return (
                <AccountGroupCard
                  key={group.accountKey}
                  variant="invoice"
                  group={group}
                  groupAllocation={groupAllocation}
                  showItemDetails={isPartial && group.items.length > 1}
                  isWirePayment={isWirePayment}
                  isMultiGroup={isMultiGroup}
                  groupWireNumbers={groupWireNumbers}
                  selectedAllocationMethod={selectedAllocationMethod}
                  itemAllocations={itemAllocations}
                  updateAccountAllocation={updateAccountAllocation}
                  updateGroupWireNumber={updateGroupWireNumber}
                  updateItemAllocation={updateItemAllocation}
                />
              );
            })}
            {isMultiGroup && (
              <Row xBetween className="border-t pt-2">
                <span className="text-sm font-semibold">
                  {t.invoices.paymentDrawer.fields.totalAmount}
                </span>
                <span className="text-sm font-semibold">
                  {formatMoney(paymentAmount, invoices[0].amount.currencyCode)}
                </span>
              </Row>
            )}
          </Col>
        </DialogMain>

        <DialogFooter>
          <Row xEnd gap3 className="mt-auto">
            <Button
              variant="outline"
              onPress={handleClose}
              isDisabled={isSubmitting}
            >
              {t.invoices.paymentDrawer.buttons.cancel}
            </Button>
            <Button
              variant="solid"
              color="primary"
              onPress={handleSubmit}
              isPending={isSubmitting}
              isDisabled={!canSubmit}
            >
              {t.invoices.paymentDrawer.buttons.receive}
            </Button>
          </Row>
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
};
